import { useMemo, useRef } from 'react';
import FavoriteFilterModal from '@/components/pools/list/filters/favoriteFilters/FavoriteFilterModal';
import type { FavoriteFilterModalHandle } from '@/components/pools/list/filters/favoriteFilters/FavoriteFilterModal';
import ButtonsDropdown from '@/components/ui/ButtonsDropdown';
import type { ButtonsDropdownOption } from '@/components/ui/ButtonsDropdown';
import {
  useFavoriteFilters,
  useDeleteFavoriteFilter,
  useUpdateFavoriteFilter,
} from '@/hooks/queries/useFavoriteFiltersQueries';
import { FavoriteFilter } from '@/services/favoriteFiltersService';
import { PoolFilters } from '@/types/pool';
import FilterSelectorDropdown from './FavoriteFiltersDropdown';

type InvestorFiltersHeaderSectionProps = {
  onLoadFilter: (filters: PoolFilters) => void;
  getCurrentFilters: () => PoolFilters;
  hasFiltersChanged: boolean;
  activeFilterId: number | null;
  onActiveFilterChange: (id: number | null) => void;
};

export default function InvestorFiltersHeaderSection({
  onLoadFilter,
  getCurrentFilters,
  hasFiltersChanged,
  activeFilterId,
  onActiveFilterChange,
}: InvestorFiltersHeaderSectionProps) {
  const { data: savedFiltersList } = useFavoriteFilters();
  const deleteFilter = useDeleteFavoriteFilter();
  const updateFilter = useUpdateFavoriteFilter();
  const favoriteFilterModalRef = useRef<FavoriteFilterModalHandle>(null);

  const filtersArray = useMemo(() => savedFiltersList?.filters ?? [], [savedFiltersList?.filters]);
  const selectedFilter = filtersArray.find((f) => f.id === activeFilterId) ?? null;
  const isUpdateDisabled = !selectedFilter || !hasFiltersChanged;

  const handleOpenCreateModal = () => {
    favoriteFilterModalRef.current?.open();
  };

  const handleFilterSelect = (filter: FavoriteFilter) => {
    onActiveFilterChange(filter.id);
    onLoadFilter(filter.filters);
  };

  const handleDeleteFilter = (filterId: number) => {
    if (activeFilterId === filterId) {
      onActiveFilterChange(null);
    }
    deleteFilter.mutate(filterId);
  };

  const handleUpdateFilter = () => {
    if (!selectedFilter) return;

    updateFilter.mutate(
      {
        filterId: selectedFilter.id,
        name: selectedFilter.name,
        filters: getCurrentFilters(),
      },
      {
        onSuccess: () => {
          favoriteFilterModalRef.current?.openUpdateSuccess();
        },
      }
    );
  };

  const saveFilterOptions: ButtonsDropdownOption[] = [
    {
      value: 'create-new-filter',
      label: 'Create New Filter',
      onClick: handleOpenCreateModal,
    },
    {
      value: 'update-filter',
      label: 'Update',
      onClick: handleUpdateFilter,
      disabled: isUpdateDisabled,
    },
  ];

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <FilterSelectorDropdown
          filters={filtersArray}
          selectedFilterId={activeFilterId}
          onSelect={handleFilterSelect}
          onDelete={handleDeleteFilter}
        />

        <ButtonsDropdown label="Save" options={saveFilterOptions} disabled={updateFilter.isPending} />
      </div>

      <FavoriteFilterModal
        ref={favoriteFilterModalRef}
        currentFilters={getCurrentFilters()}
        onCreateSuccess={() => onActiveFilterChange(null)}
      />
    </>
  );
}
